import { useLayoutEffect, useRef, useState } from 'react';
import { generatePath, useParams } from 'react-router-dom';
import { isToday } from 'date-fns';
import { AlertCircle, CircuitBoard, ListTodo, Zap } from 'lucide-react';
import { useDebounceValue } from 'usehooks-ts';

import { Link } from '@/components/ui/link';
import { PageUrlKey } from '@/entities/page-url-key';
import { useFeatureFlag } from '@/hooks/use-feature-flag';
import { useMe } from '@/hooks/use-me';
import { AlertStatusBadge } from '@/pcs/view/components/AlertStatusBadge';
import { useCurrentAssets } from '@/siteview/hooks/use-current-assets';
import { cls } from '@/utils/cls';
import { dynamicPowerUnit } from '@/utils/dynamic-power-unit';
import { formatAmpereHours, formatEnergy } from '@/utils/format/battery';
import { siteLabel } from '@/utils/format/customer';
import { formatPercentage } from '@/utils/format/number';
import { formatTemperature } from '@/utils/format/temperature';

import { imbalanceSocColor } from '../colors/imbalance-soc';
import { socColor } from '../colors/soc-color';
import { socOffsetColor } from '../colors/soc-offset-color';
import { temperatureColor } from '../colors/temperature-color';
import { assetSocOffset } from '../entities/asset';
import type { AssetTooltip as AssetTooltipData } from '../entities/asset-tooltip';
import { AssetType } from '../entities/asset-type';
import { useAssetTooltip } from '../hooks/use-asset-tooltip';
import { useAssetsFilters } from '../hooks/use-assets-filters';

import { AssetTooltipSystemLogs } from './asset-tooltip-system-logs';

export function AssetTooltip({
  containerRef,
}: {
  containerRef: React.RefObject<HTMLDivElement>;
}) {
  const [tooltip] = useAssetTooltip();
  const [debouncedTooltip] = useDebounceValue(tooltip, 10);
  const tooltipRef = useRef<HTMLDivElement>(null);
  const [position, setPosition] = useState<{ x: number; y: number }>({
    x: 0,
    y: 0,
  });

  useLayoutEffect(() => {
    if (!debouncedTooltip) return;
    let { x, y } = debouncedTooltip.pos;

    const el = tooltipRef.current;
    const container = containerRef.current;

    if (el && container) {
      const tooltipRect = el.getBoundingClientRect();
      const containerRect = container.getBoundingClientRect();
      const tooltipHeight = tooltipRect.height;
      const tooltipWidth = tooltipRect.width;
      const containerHeight = containerRect.height;
      const containerWidth = containerRect.width;
      const paddingRight = 5;

      // Clamp bottom
      if (y + tooltipHeight > containerHeight) {
        y = containerHeight - tooltipHeight;
      }
      // Clamp right
      if (x + tooltipWidth > containerWidth) {
        x = containerWidth - tooltipWidth - paddingRight;
      }

      setPosition({ x, y });
    }
  }, [debouncedTooltip, containerRef]);

  // useMemo should return a style property for html tag
  if (!debouncedTooltip || !tooltip) {
    return null;
  }

  return (
    <div
      ref={tooltipRef}
      className={cls('absolute left-0 top-0 transition-transform duration-75', {
        'pointer-events-none': !debouncedTooltip.static,
        'pointer-events-auto': debouncedTooltip.static,
      })}
      style={{
        transform: `translate(${position.x}px, ${position.y}px)`,
        willChange: 'transform',
      }}
    >
      <AssetTooltipContent tooltip={debouncedTooltip} />
    </div>
  );
}

const queryParamsFromAssetType: Record<string, Record<AssetType, string>> = {
  '49': {
    [AssetType.CONTAINER]: 'bank_id',
    [AssetType.PCS]: '',
    [AssetType.STRING]: '',
    [AssetType.RACK]: 'pack_id',
    [AssetType.MODULE]: '',
  },
  '69': {
    [AssetType.CONTAINER]: 'container_id',
    [AssetType.PCS]: '',
    [AssetType.STRING]: 'pack_id',
    [AssetType.RACK]: '',
    [AssetType.MODULE]: '',
  },
  '61': {
    [AssetType.CONTAINER]: 'container_id',
    [AssetType.PCS]: '',
    [AssetType.STRING]: 'pack_id',
    [AssetType.RACK]: '',
    [AssetType.MODULE]: '',
  },
  '66': {
    [AssetType.CONTAINER]: 'array_id',
    [AssetType.PCS]: '',
    [AssetType.STRING]: 'pack_id',
    [AssetType.RACK]: '',
    [AssetType.MODULE]: '',
  },
};

type AssetTooltipContentProps = {
  tooltip: AssetTooltipData;
};

function AssetTooltipContent({ tooltip }: AssetTooltipContentProps) {
  const me = useMe();
  const { siteId } = useParams<{ siteId: string }>();
  const featureFlag = useFeatureFlag();
  const [assetsFilters] = useAssetsFilters();

  const [{ assetPerId }] = useCurrentAssets();
  const asset = assetPerId[tooltip.id];

  if (!asset) {
    return null;
  }

  const socOffset = assetSocOffset(asset);
  const isPending = !asset.alertStatus && isToday(assetsFilters.date);

  return (
    <div className="w-72 overflow-hidden rounded-lg border border-gray-200 bg-white p-0 shadow-lg">
      {/* Header with name and status */}
      <div className="border-b border-gray-100 p-3">
        <h3 className="break-words text-lg font-bold text-gray-900">
          {siteLabel(asset.fullFrontendId)}
        </h3>
      </div>

      {/* Battery section */}
      <div className="border-b border-gray-100 p-3">
        <div className="mb-2 flex items-center">
          <AlertCircle className="mr-2 size-4 text-gray-500" />
          <h4 className="text-sm font-semibold text-gray-700">
            Battery Status
          </h4>
        </div>
        <div className="flex items-center justify-between">
          <span className="text-sm text-gray-600">Alert Status</span>
          <div className="flex items-center gap-2">
            {featureFlag('details') &&
            queryParamsFromAssetType[me.config.id]?.[asset.type] ? (
              <Link
                to={{
                  pathname: generatePath(
                    `/asset/:siteId/${PageUrlKey.DETAILS}`,
                    {
                      siteId: siteId,
                    },
                  ),
                  search: new URLSearchParams({
                    [queryParamsFromAssetType[me.config.id][asset.type]]:
                      asset.fullFrontendId,
                  }).toString(),
                }}
              >
                <AlertStatusBadge
                  status={asset.alertStatus}
                  pending={isPending}
                />
              </Link>
            ) : (
              <AlertStatusBadge
                status={asset.alertStatus}
                pending={isPending}
              />
            )}
          </div>
        </div>
        {featureFlag('systemLogs') && (
          <AssetTooltipSystemLogs
            siteId={siteId}
            asset={asset}
            unitAccureId={tooltip.id}
          />
        )}
      </div>

      {/* Performance section */}
      {typeof asset.accureSoc === 'number' ||
      typeof asset.bmsSoc === 'number' ||
      typeof socOffset === 'number' ? (
        <div className="border-b border-gray-100 p-3">
          <div className="mb-2 flex items-center">
            <Zap className="mr-2 size-4 text-gray-500" />
            <h4 className="text-sm font-semibold text-gray-700">Performance</h4>
          </div>

          {/* ACCURE SOC */}
          {typeof asset.accureSoc === 'number' ? (
            <div className="mb-2 flex items-center justify-between">
              <span className="text-sm text-gray-600">ACCURE SOC</span>
              <div className="flex items-center">
                <span
                  className="mr-2 text-sm font-medium"
                  style={{ color: socColor(asset.accureSoc) }}
                >
                  {formatPercentage(asset.accureSoc, 0, 1)}
                </span>
                <div className="h-2 w-10 overflow-hidden rounded-full bg-gray-200">
                  <div
                    className="h-full"
                    style={{
                      width: `${asset.accureSoc * 100}%`,
                      background: socColor(asset.accureSoc),
                    }}
                  ></div>
                </div>
              </div>
            </div>
          ) : null}

          {/* BMS SOC */}
          {typeof asset.bmsSoc === 'number' ? (
            <div className="mb-2 flex items-center justify-between">
              <span className="text-sm text-gray-600">BMS SOC</span>
              <div className="flex items-center">
                <span
                  className="mr-2 text-sm font-medium"
                  style={{ color: socColor(asset.bmsSoc) }}
                >
                  {formatPercentage(asset.bmsSoc, 0, 1)}
                </span>
                <div className="h-2 w-10 overflow-hidden rounded-full bg-gray-200">
                  <div
                    className="h-full"
                    style={{
                      width: `${asset.bmsSoc * 100}%`,
                      background: socColor(asset.bmsSoc),
                    }}
                  ></div>
                </div>
              </div>
            </div>
          ) : null}

          {/* SOC Offset */}
          {typeof socOffset === 'number' ? (
            <div className="mb-2 flex items-center justify-between">
              <div className="flex items-center">
                <span className="text-sm text-gray-600">SOC Offset</span>
              </div>
              <span
                className="text-sm font-medium"
                style={{
                  color: socOffsetColor(socOffset),
                }}
              >
                {socOffset > 0 ? '+' : ''}
                {formatPercentage(socOffset, 0, 1)}
              </span>
            </div>
          ) : null}

          {/* IMBALANCE SOC */}
          {typeof asset.imbalanceSoc === 'number' ? (
            <div className="mb-2 flex items-center justify-between">
              <div className="flex items-center">
                <span className="text-sm text-gray-600">Imbalance SOC</span>
              </div>
              <span
                className="text-sm font-medium"
                style={{
                  color: imbalanceSocColor(asset.imbalanceSoc, 0.75),
                }}
              >
                {formatPercentage(asset.imbalanceSoc, 0, 1)}
              </span>
            </div>
          ) : null}

          {/* Temperature */}
          {typeof asset.temperature === 'number' ? (
            <div className="mb-2 flex items-center justify-between">
              <div className="flex items-center">
                <span className="text-sm text-gray-600">Temperature</span>
              </div>
              <span
                className="text-sm font-medium"
                style={{
                  color: temperatureColor(asset.temperature, 0.75),
                }}
              >
                {formatTemperature(asset.temperature, 0, 1)}
              </span>
            </div>
          ) : null}

          {/* Throughput */}
          {typeof asset.throughput === 'number' ? (
            <div className="mb-2 flex items-center justify-between">
              <div className="flex items-center">
                <span className="text-sm text-gray-600">Throughput</span>
              </div>
              <span className="text-sm font-medium text-gray-900">
                {formatEnergy(
                  asset.throughput,
                  dynamicPowerUnit(asset.throughput),
                  0,
                  1,
                )}
              </span>
            </div>
          ) : null}
        </div>
      ) : null}

      {/* Tasks section */}
      {asset.nbOpenTasks && asset.nbOpenTasks > 0 ? (
        <div className="border-b border-gray-100 p-3">
          <div className="mb-2 flex items-center">
            <ListTodo className="mr-2 size-4 text-gray-500" />
            <h4 className="text-sm font-semibold text-gray-700">Maintenance</h4>
          </div>

          <div className="flex items-center justify-between">
            <span className="text-sm text-gray-600">Open Tasks</span>
            <Link
              to="/tasks"
              className="flex cursor-pointer items-center text-sm font-medium text-primary-600 hover:underline"
            >
              {asset.nbOpenTasks} tasks
              <svg
                className="ml-1 size-3"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path
                  fillRule="evenodd"
                  d="M5.22 14.78a.75.75 0 001.06 0l7.22-7.22v5.69a.75.75 0 001.5 0v-7.5a.75.75 0 00-.75-.75h-7.5a.75.75 0 000 1.5h5.69l-7.22 7.22a.75.75 0 000 1.06z"
                  clipRule="evenodd"
                />
              </svg>
            </Link>
          </div>
        </div>
      ) : null}

      {/* Specifications section */}
      {asset.metadata.capacity ||
      asset.metadata.cellChemistry ||
      asset.metadata.cellType ||
      asset.metadata.deviceType ||
      asset.metadata.manufacturer ? (
        <div className="border-t border-gray-100 bg-gray-50 px-3 py-2.5">
          <div className="mb-2 flex items-center">
            <CircuitBoard className="mr-1.5 size-4 text-gray-400" />
            <h4 className="text-xs font-medium text-gray-500">
              Specifications
            </h4>
          </div>

          <div className="grid grid-cols-2 gap-x-3 gap-y-2">
            {asset.metadata.capacity ? (
              <Specification
                label="Capacity"
                value={formatAmpereHours(asset.metadata.capacity, 0, 1)}
              />
            ) : null}
            {asset.metadata.cellChemistry ? (
              <Specification
                label="Cell Chemistry"
                value={asset.metadata.cellChemistry}
              />
            ) : null}
            {asset.metadata.cellType ? (
              <Specification
                label="Cell Type"
                value={asset.metadata.cellType}
              />
            ) : null}
            {asset.metadata.deviceType ? (
              <Specification
                label="Device Type"
                value={asset.metadata.deviceType}
              />
            ) : null}
            {asset.metadata.manufacturer ? (
              <Specification
                label="Device Type"
                value={asset.metadata.manufacturer}
              />
            ) : null}
          </div>
        </div>
      ) : null}
    </div>
  );
}

type SpecificationProps = {
  label: string;
  value: string;
};

function Specification({ label, value }: SpecificationProps) {
  return (
    <div>
      <span className="block text-xs text-gray-400">{label}</span>
      <span className="break-words text-xs font-medium text-gray-600">
        {value}
      </span>
    </div>
  );
}
